import React from "react";
import { useNavigate, useLocation } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// layouts
import FormField from "layouts/ecommerce/products/edit-product/components/FormField";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// redux
import { useSelector, useDispatch } from "react-redux";
import * as actions from "redux/actions/schedule";

// ScheduleDto: {id, userId, title, startTime, endTime, description} — the
// old Task/Time dropdown-lookup model is gone, replaced by direct fields.
const emptyForm = {
  id: 0,
  title: "",
  startTime: "",
  endTime: "",
  description: "",
};

const toDateTimeInputValue = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 16);
};

const ScheduleForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const editData = location.state?.editData;
  const schedule = useSelector((state) => state.schedule);

  const [form, setForm] = React.useState(
    editData
      ? {
          ...emptyForm,
          ...editData,
          startTime: toDateTimeInputValue(editData.startTime),
          endTime: toDateTimeInputValue(editData.endTime),
        }
      : emptyForm
  );

  React.useEffect(() => {
    if (schedule.scheduleSaved) navigate(`/upload/schedule`);
  }, [schedule, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    if (!form.title || !form.startTime || !form.endTime) {
      alert("Title, start time and end time are required.");
      return;
    }
    const payload = {
      ...form,
      startTime: new Date(form.startTime).toISOString(),
      endTime: new Date(form.endTime).toISOString(),
    };
    if (editData) dispatch(actions.editSchedule(payload));
    else dispatch(actions.addSchedule(payload));
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Grid container>
        <Grid item xs={12} md={8} xl={6}>
          <MDBox py={3} pr={1}>
            <Card sx={{ width: "100%" }}>
              <MDBox display="flex">
                <MDBox
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  width="4rem"
                  height="4rem"
                  variant="gradient"
                  bgColor="primary"
                  color="white"
                  shadow="md"
                  borderRadius="xl"
                  ml={3}
                  mt={-2}
                >
                  <Icon fontSize="medium" color="inherit">
                    add
                  </Icon>
                </MDBox>
                <MDBox>
                  <MDTypography variant="h6" sx={{ mt: 2, mb: 1, ml: 2 }}>
                    {editData ? "Edit Schedule" : "Add Schedule"}
                  </MDTypography>
                </MDBox>
              </MDBox>
              <MDBox p={3}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormField
                      type="text"
                      label="Title"
                      name="title"
                      value={form.title}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormField
                      type="datetime-local"
                      label="Start Time"
                      name="startTime"
                      value={form.startTime}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormField
                      type="datetime-local"
                      label="End Time"
                      name="endTime"
                      value={form.endTime}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormField
                      type="text"
                      label="Description"
                      name="description"
                      value={form.description}
                      onChange={handleChange}
                      multiline
                      rows={4}
                    />
                  </Grid>
                </Grid>

                <MDBox mt={3} display="flex" justifyContent="end">
                  <MDButton
                    color="primary"
                    onClick={!schedule.savingSchedule ? handleSubmit : undefined}
                  >
                    {schedule.savingSchedule ? "Submitting..." : "Submit"}
                  </MDButton>
                </MDBox>
              </MDBox>
            </Card>
          </MDBox>
        </Grid>
      </Grid>
    </DashboardLayout>
  );
};

export default ScheduleForm;
